import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export function BuyCredits() {
  const [plans, setPlans] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [params] = useSearchParams();
  const selected = params.get("plan");
  const [selectedPlan, setSelectedPlan] = useState(selected || null);
  const [gateway, setGateway] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data));
    api.get("/gateways/public").then((r) => {
      setGateways(r.data);
      if (r.data?.[0]) setGateway(r.data[0].name);
    });
  }, []);

  const startCheckout = async () => {
    if (!user) { nav("/login?next=" + encodeURIComponent("/buy-credits")); return; }
    if (!selectedPlan) { toast.error("Select a plan"); return; }
    if (!gateway) { toast.error("No payment gateway is enabled yet. Please contact support."); return; }
    try {
      const { data } = await api.post("/credits/purchase", { plan_slug: selectedPlan, gateway });
      // Stripe checkout: redirect to gateway URL
      if (gateway === "stripe" && data.extra?.checkout_url) {
        window.location.href = data.extra.checkout_url;
        return;
      }
      // Razorpay: open JS checkout if a razorpay_order_id is returned
      if (gateway === "razorpay" && data.extra?.razorpay_order_id) {
        await openRazorpay(data);
        return;
      }
      // PayGlocal: redirect to provided URL
      if (gateway === "payglocal" && data.extra?.checkout_url) {
        window.location.href = data.extra.checkout_url;
        return;
      }
      // Fallback: in-app confirm
      nav(`/buy-credits/checkout/${data.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    }
  };

  const openRazorpay = async (data) => {
    // Inject Razorpay script if not loaded
    if (!window.Razorpay) {
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = resolve; s.onerror = reject;
        document.body.appendChild(s);
      });
    }
    const ext = data.extra;
    const rzp = new window.Razorpay({
      key: ext.razorpay_key_id,
      order_id: ext.razorpay_order_id,
      amount: ext.amount,
      currency: ext.currency,
      name: "BINAREX.CO",
      description: "AI Credits",
      prefill: { email: user.email, name: user.name },
      theme: { color: "#8B5CF6" },
      handler: async (resp) => {
        try {
          await api.post("/credits/confirm-razorpay", {
            order_id: data.order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_signature: resp.razorpay_signature,
          });
          nav(`/buy-credits/checkout/${data.order_id}?provider=razorpay&status=success`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Razorpay confirm failed");
        }
      },
    });
    rzp.open();
  };

  return (
    <MarketingLayout>
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
        <div className="bx-chip"><Lucide.Wallet size={12} /> BUY CREDITS</div>
        <h1 className="bx-heading mt-3 text-3xl sm:text-4xl font-extrabold">Top up your credit wallet</h1>
        <p className="text-slate-400 mt-2">Pick a plan and a payment gateway to instantly add credits to your account.</p>

        <div className="mt-8 grid lg:grid-cols-5 gap-5">
          {plans.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedPlan(p.slug)}
              data-testid={`buy-plan-${p.slug}`}
              className={`bx-card p-5 text-left ${selectedPlan === p.slug ? "border-purple-500 shadow-[0_20px_60px_-15px_rgba(139,92,246,0.45)]" : ""}`}
            >
              <div className="text-sm">{p.name.replace(" Credits", "")}</div>
              <div className="bx-heading text-2xl font-extrabold mt-1">${p.price_usd}</div>
              <div className="text-xs text-purple-300 font-mono mt-0.5">{p.credits.toLocaleString()} credits</div>
              <div className="text-[11px] text-slate-500 mt-1">{p.validity_days} days validity</div>
            </button>
          ))}
        </div>

        <div className="mt-10">
          <div className="text-sm font-semibold text-slate-200">Payment Gateway</div>
          <p className="text-xs text-slate-500">Gateways are configured by admin from the Admin → Payment Gateways panel. Enable a gateway with valid API keys to receive payments.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {gateways.length === 0 && (
              <div className="bx-card p-5 w-full text-sm text-amber-300/90 flex items-center gap-2">
                <Lucide.AlertCircle size={16} />
                No payment gateways enabled yet. Admin needs to configure Razorpay, Stripe, or PayGlocal in the Admin Panel.
              </div>
            )}
            {gateways.map((g) => (
              <button
                key={g.name}
                onClick={() => setGateway(g.name)}
                data-testid={`buy-gateway-${g.name}`}
                className={`bx-card px-5 py-3 capitalize flex items-center gap-2 ${gateway === g.name ? "border-purple-500" : ""}`}
              >
                <Lucide.CreditCard size={16} /> {g.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button data-testid="buy-checkout-btn" onClick={startCheckout} className="bx-btn-primary">
            <Lucide.ShoppingCart size={16} /> Proceed to Checkout
          </button>
          <Link to="/pricing" className="bx-btn-ghost">Back to Pricing</Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

export function Checkout() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  const provider = params.get("provider");
  const status = params.get("status");

  useEffect(() => {
    // Stripe returns here with status=success — confirm server-side
    if (provider === "stripe" && status === "success") {
      (async () => {
        try {
          setBusy(true);
          await api.post(`/credits/confirm/${orderId}`);
          await refresh();
          setDone(true);
          toast.success("Payment successful! Credits added to your account.");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not confirm payment");
        } finally { setBusy(false); }
      })();
    }
  }, [provider, status, orderId, refresh]);

  const confirmMock = async () => {
    setBusy(true);
    try {
      await api.post(`/credits/confirm/${orderId}`);
      await refresh();
      setDone(true);
      toast.success("Payment successful! Credits added to your account.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingLayout>
      <section className="max-w-2xl mx-auto px-5 lg:px-8 py-16">
        <div className="bx-card p-8 text-center">
          {!done ? (
            <>
              <Lucide.CreditCard size={36} className="mx-auto text-purple-300" />
              <h1 className="bx-heading mt-3 text-2xl font-extrabold">Complete your payment</h1>
              <p className="text-slate-400 mt-2 text-sm">
                Order ID: <span className="bx-mono text-purple-300">{orderId}</span>
              </p>
              {provider === "stripe" && status === "cancel" && (
                <p className="mt-3 text-sm text-rose-300">Stripe payment was cancelled.</p>
              )}
              <p className="text-slate-400 mt-4 text-sm max-w-md mx-auto">
                If your gateway is configured (Stripe/Razorpay/PayGlocal), you've already been redirected.
                If the admin has not yet added live keys for the selected gateway, click below to simulate a successful payment for this order.
              </p>
              <button data-testid="checkout-confirm" onClick={confirmMock} disabled={busy} className="bx-btn-primary mt-6">
                {busy ? <Lucide.Loader2 className="animate-spin" size={16} /> : <Lucide.Check size={16} />} Confirm Payment
              </button>
            </>
          ) : (
            <>
              <Lucide.CheckCircle2 size={48} className="mx-auto text-emerald-400" />
              <h1 className="bx-heading mt-3 text-2xl font-extrabold">Payment Successful</h1>
              <p className="text-slate-400 mt-2 text-sm">Credits have been added to your wallet.</p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => nav("/dashboard")} className="bx-btn-primary">Go to Dashboard</button>
                <Link to="/tools" className="bx-btn-ghost">Try a Tool</Link>
              </div>
            </>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
